import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUpload,
  faFile,
  faTrashCan,
  faQrcode,
  faCopy,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { QRCodeSVG } from "qrcode.react";
import { ButtonSoft } from "../atoms/ButtonSoft";
import { toasts } from "@/shared/utils/toasts";
import { uploadFile } from "@/app/progreso/progress.api";
import { DeleteModal } from "./DeleteModal";
import { t } from "i18next";

const RenderFormUploadList = ({
  accept = "image/*",
  enpoint,
  refetchList,
  listImage,
  listLoader,
  user_id,
  deleteAction,
}) => {
  const [files, setFiles] = useState([]);
  const [preview, setPreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const fileInputRef = useRef(null);

  const [deleteModal, setDeleteModal] = useState(false);
  const [selectElemet, setselectElemet] = useState();

  const QR_URL = "https://test.fitmewise.com/admin/progress-image/upload/25256";

  // Handle file selection with PDF validation
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (
        !accept.includes("image") &&
        selectedFile.type !== "application/pdf"
      ) {
        //@ts-ignore
        toasts.success("url copiada correctamente");
        fileInputRef.current.value = "";
        return;
      }
      const previewUrl = URL.createObjectURL(selectedFile);
      setPreview({ file: selectedFile, url: previewUrl });
    }
  };

  // Perform the file upload immediately
  const handleUpload = async () => {
    if (preview && !isUploading) {
      setIsUploading(true);
      try {
        const payload = {
          file: preview.file,
          field: {
            user_id: user_id,
            anonymous: false,
          },
          emdPoin: enpoint,
        };

        const result = await uploadFile(payload);

        toasts.success("Archivo subido exitosamente");

        if (refetchList) refetchList();
        setPreview(null);
        fileInputRef.current.value = "";
      } catch (err) {
        toasts.error("Error al subir el archivo");
      } finally {
        setIsUploading(false);
      }
    }
  };

  // Remove file from list
  const handleRemove = (id) => {
    setselectElemet(id);
    setDeleteModal(true); // Store the ID to delete
  };

  useEffect(() => {
    if (Array.isArray(listImage)) {
      const formattedFiles = listImage.map((item) => ({
        id: item.id,
        file: { name: item.original_file_name },
        url: `${item?.image_url ?? item?.file_path}`,
        date: new Date(item.created_at),
      }));
      setFiles(formattedFiles);
    }
  }, [listImage]);

  // Open file in new tab
  const handleView = (url) => {
    if (url) {
      window.open(url, "_blank");
    }
  };

  // Copy URL to clipboard
  const handleCopy = () => {
    navigator.clipboard.writeText(QR_URL).then(() => {
      //@ts-ignore
      toasts.success("url copiada correctamente");
    });
  };

  // Format date for display
  const formatDate = (date) => {
    return date.toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  // Determine input accept value
  const inputAccept = accept.includes("image") ? "image/*" : "application/pdf";

  // Handle outside click to close modal
  const handleOutsideClick = (e) => {
    if (e.target === e.currentTarget) {
      setShowQRCode(false);
    }
  };

  return (
    <motion.div
      className="max-w-lg mx-auto p-6 bg-white"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="flex flex-col items-center">
        {accept.includes("image") && (
          <div className="flex justify-center gap-4 mb-3">
            <motion.button
              className="text-primary"
              onClick={() => setShowQRCode(true)}
              title="Show QR Code"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FontAwesomeIcon icon={faQrcode} size="lg" />
            </motion.button>
            <motion.button
              className="text-primary "
              onClick={handleCopy}
              title="Copy URL"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FontAwesomeIcon icon={faCopy} size="lg" />
            </motion.button>
          </div>
        )}
        <div
          className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer bg-gray-50 hover:border-blue-500 hover:bg-blue-50 transition-all duration-300 w-full"
          onClick={() => fileInputRef.current.click()}
        >
          <FontAwesomeIcon
            icon={faUpload}
            className="mx-auto text-gray-500"
            size="lg"
          />
          <p className="mt-2 text-gray-600">
            Click to select {accept.includes("image") ? "an image" : "a PDF"}
          </p>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept={inputAccept}
            className="hidden"
          />
        </div>
      </div>

      <AnimatePresence>
        {preview && (
          <motion.div
            className="mt-5 flex items-center gap-4 flex-col sm:flex-row"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex-1 max-w-[200px]">
              {accept.includes("image") && preview.url ? (
                <div className="max-w-[200px] max-h-[200px] rounded-lg overflow-hidden border border-gray-200 shadow-md bg-white">
                  <img
                    src={preview.url}
                    alt="Preview"
                    className="w-full h-full object-contain"
                  />
                </div>
              ) : (
                <div className="max-w-[200px] p-4 bg-gray-100 rounded-lg border border-gray-200 shadow-md text-center">
                  <FontAwesomeIcon
                    icon={faFile}
                    className="mx-auto text-gray-600"
                    size="lg"
                  />
                  <p className="mt-2 text-gray-700 truncate">
                    {preview.file.name}
                  </p>
                </div>
              )}
            </div>
            <ButtonSoft
              onClick={handleUpload}
              disabled={isUploading}
              size="large"
              text={
                "Subir " + (accept.includes("image") ? "imagen" : "archivo")
              }
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* QR Code Modal */}
      {showQRCode && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleOutsideClick}
        >
          <motion.div
            className="bg-white p-6 rounded-lg shadow-lg"
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.8 }}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">QR Code</h3>
              <button
                className="text-gray-600 hover:text-gray-800"
                onClick={() => setShowQRCode(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <QRCodeSVG value={QR_URL} size={200} />
          </motion.div>
        </motion.div>
      )}

      <AnimatePresence>
        {files.length > 0 && (
          <motion.div
            className="mt-5 w-full"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="w-full max-h-96 overflow-y-auto">
              {files.map((file) => (
                <motion.div
                  key={file.id}
                  className="flex items-center justify-between p-3 bg-gray-100 rounded-md mb-2 text-sm gap-3"
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: 20, opacity: 0 }}
                >
                  <div className="flex items-center gap-3 flex-1">
                    {accept.includes("image") && file.url ? (
                      <div className="w-12 h-12 rounded-md overflow-hidden border border-gray-200">
                        <img
                          src={file.url}
                          alt="Thumbnail"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    ) : (
                      <FontAwesomeIcon
                        icon={faFile}
                        className="text-gray-600 w-12 h-12 p-2"
                      />
                    )}
                    <div className="flex-1">
                    <span className="text-gray-700 block text-sm line-clamp-2 break-words">
  {file.file.name}
</span>

                      <span className="text-gray-500 text-xs">
                        {formatDate(file.date)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <ButtonSoft
                      onClick={() => {
                        handleView(file?.url ?? file.file_path);
                      }}
                      text="Ver"
                    />
                    <button
                      className="text-red-500 hover:text-red-600 p-1"
                      onClick={() => handleRemove(file.id)}
                    >
                      <FontAwesomeIcon icon={faTrashCan} />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <DeleteModal
        isOpen={!!deleteModal}
        onDelete={async () => {
          await deleteAction({ image_id: selectElemet });
          setFiles((prev) => prev.filter((file) => file.id !== selectElemet));
          setDeleteModal(false);
        }}
        onClose={() => setDeleteModal(false)}
        itemName={
          listImage.find((item) => item.id === selectElemet)?.name ?? ""
        }
        deleteText={t("general.delete")}
        infoText={t("delete_confirmation")}
      />
    </motion.div>
  );
};

export default RenderFormUploadList;
